/*
  ForgotPasswordForm — lightweight form that asks Resend to mail a reset link.
  Intentionally always shows "check your email" regardless of whether the
  email exists, to prevent account enumeration.
*/
import { useState } from "react";
import { Mail, Loader2, ArrowLeft, CheckCircle2 } from "lucide-react";
import { auth } from "../lib/authApi";

export default function ForgotPasswordForm({ initialEmail = "", onBack }) {
  const [email, setEmail] = useState(initialEmail || "");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  async function onSubmit(e) {
    e.preventDefault();
    setBusy(true);
    try {
      await auth.forgot(email.trim());
      setDone(true);
    } catch {
      // Swallow — surface the same "done" state no matter what (prevents enumeration).
      setDone(true);
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <div className="space-y-4" data-testid="forgot-done">
        <div className="flex items-start gap-3 rounded-lg p-3"
             style={{ background: "color-mix(in srgb, var(--success) 12%, transparent)", color: "var(--success)" }}>
          <CheckCircle2 className="h-5 w-5 shrink-0 mt-0.5" />
          <div className="text-sm">
            If an account exists for <strong>{email || "that email"}</strong>, we've just sent a reset link.
            Check your inbox (and spam folder).
          </div>
        </div>
        <button
          type="button"
          onClick={onBack}
          data-testid="forgot-back"
          className="w-full h-10 rounded-lg text-sm font-semibold border inline-flex items-center justify-center gap-2"
          style={{ borderColor: "var(--border-subtle)", color: "var(--text-main)" }}
        >
          <ArrowLeft className="h-4 w-4" /> Back to sign in
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="space-y-3" data-testid="forgot-form">
      <label className="block">
        <span className="label-caps block mb-1">Email</span>
        <div className="relative">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: "var(--text-muted)" }} />
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="forgot-email-input"
            className="w-full pl-9 pr-3 h-10 rounded-lg border bg-transparent text-sm"
            style={{ borderColor: "var(--border-subtle)", color: "var(--text-main)" }}
          />
        </div>
      </label>
      <button
        type="submit"
        disabled={busy}
        data-testid="forgot-submit"
        className="w-full h-10 rounded-lg text-sm font-semibold text-white inline-flex items-center justify-center gap-2 disabled:opacity-60"
        style={{ background: "var(--primary)" }}
      >
        {busy && <Loader2 className="h-4 w-4 animate-spin" />} Send reset link
      </button>
      <button
        type="button"
        onClick={onBack}
        className="block text-xs mx-auto hover:underline pt-1"
        style={{ color: "var(--text-muted)" }}
      >Back to sign in</button>
    </form>
  );
}
